<script lang="ts" setup>
import { Calendar } from '@element-plus/icons-vue'
import { NoteComponent } from '@metapic-apps/shared'
import { computed, watch } from 'vue'

import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = defineProps<{
  id: string
  currencySymbol: string
  hasHashtags?: boolean
}>()
const hasStartNow = defineModel<boolean>('hasStartNow')
const hasOpenEnd = defineModel<boolean>('hasOpenEnd')
const startDate = defineModel<string>('startDate')
const endDate = defineModel<string>('endDate')
const hasIndividualLimitPerUser = defineModel<boolean>('hasIndividualLimitPerUser')
const maxClicks = defineModel<number>('maxClicks')
const defaultTime = computed(() => {
  const date = new Date()
  date.setHours(date.getHours() + Math.round(date.getMinutes() / 60))
  date.setMinutes(0, 0, 0) // Resets also seconds and milliseconds

  return date
})

const DEFAULT_END_TIME_HOURS = 23
const DEFAULT_END_TIME_MINUTES = 59

const disableStartDate = (date) => {
  return date.getTime() < Date.now() - 8.64e7 // number of milliseconds of a day
}
const disableEndDate = (date) => {
  if (startDate.value) {
    return date.getTime() < new Date(startDate.value).getTime()
  }
  return disableStartDate(date)
}

const onEndDateChange = (val: string) => {
  const dateValue = new Date(val)
  if (dateValue.toTimeString().startsWith('00:00:00')) {
    dateValue.setHours(DEFAULT_END_TIME_HOURS, DEFAULT_END_TIME_MINUTES, 0, 0)
  }
  endDate.value = dateValue.toISOString()
}

watch(
  () => props.hasHashtags,
  (hasHashtags) => {
    if (hasHashtags) {
      hasOpenEnd.value = false
    }
  },
  { immediate: true },
)
</script>
<template>
  <BoxComponent :id="id" :mb="32" :mb-lg="48" :p="24" class="campaign-form__block">
    <BoxComponent :mb="32">
      <TypographyComponent id="campaign-time-section" variant="headline-medium">Start/End</TypographyComponent>
    </BoxComponent>
    <BoxComponent :mb="32">
      <BoxComponent :mb="24">
        <TypographyComponent text-align="left" variant="headline-small">Start</TypographyComponent>
      </BoxComponent>
      <el-radio-group v-model="hasStartNow">
        <el-radio :label="true" size="large">
          <TypographyComponent is="span" text-align="left" variant="label-large" color="initial"> Start now </TypographyComponent>
        </el-radio>
        <el-radio :label="false" size="large">
          <TypographyComponent is="span" text-align="left" variant="label-large" color="initial"> Start on date </TypographyComponent>
        </el-radio>
      </el-radio-group>
      <BoxComponent v-if="!hasStartNow" :mt="24">
        <el-form-item prop="startDate">
          <el-date-picker
            v-model="startDate"
            type="datetime"
            size="large"
            placeholder="Select start date"
            :default-value="defaultTime"
            :prefix-icon="Calendar"
            :disabled-date="disableStartDate"
            class="w-100"
          />
          <BoxComponent>
            <TypographyComponent is="span" variant="body-small">Based on UTC +01:00</TypographyComponent>
          </BoxComponent>
        </el-form-item>
      </BoxComponent>
    </BoxComponent>
    <BoxComponent :mb="32">
      <BoxComponent :mb="24">
        <TypographyComponent text-align="left" variant="headline-small">End</TypographyComponent>
      </BoxComponent>
      <BoxComponent v-if="hasHashtags" :mb="24">
        <NoteComponent variant="body-medium"> Hashtag content fetching requires the campaign to end on an event. </NoteComponent>
      </BoxComponent>
      <el-radio-group v-model="hasOpenEnd">
        <el-radio v-if="!hasHashtags" :label="true" size="large">
          <TypographyComponent is="span" text-align="left" variant="label-large" color="inherit"> Open end </TypographyComponent>
        </el-radio>
        <el-radio :label="false" size="large">
          <TypographyComponent is="span" text-align="left" variant="label-large" color="initial"> End on event </TypographyComponent>
        </el-radio>
      </el-radio-group>
      <template v-if="!hasOpenEnd">
        <BoxComponent :mt="24" :mb="24">
          <el-form-item v-if="!hasOpenEnd" prop="endDate">
            <TypographyComponent variant="title-large">End on date</TypographyComponent>
            <el-date-picker
              v-model="endDate"
              type="datetime"
              size="large"
              placeholder="Select end date"
              :default-value="defaultTime"
              class="w-100"
              :prefix-icon="Calendar"
              :disabled-date="disableEndDate"
              @update:model-value="onEndDateChange"
            />
            <BoxComponent>
              <TypographyComponent is="span" variant="body-small">Based on UTC +01:00</TypographyComponent>
            </BoxComponent>
          </el-form-item>
        </BoxComponent>
        <BoxComponent :mb="16">
          <TypographyComponent variant="title-large">or click limit</TypographyComponent>
          <el-form-item prop="maxClicks">
            <el-input v-model.number="maxClicks" size="large" type="number" step="any" class="campaign-form--clicks-input">
              <template #suffix>
                <TypographyComponent variant="title-large" style="margin-bottom: 0">
                  {{ hasIndividualLimitPerUser ? 'Clicks / creator' : 'Clicks / all creators' }}
                </TypographyComponent>
              </template>
            </el-input>
          </el-form-item>
        </BoxComponent>
        <BoxComponent>
          <FlexComponent justify="space-between" align="center">
            <FlexComponent direction="column">
              <TypographyComponent variant="title-large" style="margin-bottom: 4px">Set limit for each creator</TypographyComponent>
              <TypographyComponent variant="body-small">
                By default, the click limit counts for all creators and shared links. Choose here whether the limit should count for each
                creator individually.
              </TypographyComponent>
            </FlexComponent>
            <el-switch v-model="hasIndividualLimitPerUser" size="large" />
          </FlexComponent>
        </BoxComponent>
      </template>
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.text-color {
  color: var(--primary-color);
}
</style>
